import { Box } from '@mui/material';
import { CATEGORY_IDS } from '@timelog/pm-shared';
import { useTranslation } from 'react-i18next';

import { CATEGORY_COLORS } from './categoryStyles';
import { useFinancials } from './editor';

import { MICRO_LABEL_TRACKING } from '@/components/ui';
import { formatAmountAbbrev } from '@/lib';

/**
 * The active contract's revenue-distribution bar: one segment per distributed
 * category (BR-10/BR-9/BR-27 excluded), a grey remainder for what is not yet
 * allocated and a coral overflow past 100% (CALC-14), with the legend and the
 * remaining amount beneath.
 *
 * It sits in the contract editor's totals strip, beside the total amount. From
 * 2026-09-29 to 2026-10-14 it opened the aside's Contract summary instead; the
 * ids (`distribution-bar`, `distribution-overflow`) travelled both ways.
 */
export const RevenueDistribution = () => {
  const { state, activeFinancials } = useFinancials();
  const { revenue } = state.document;
  const { t } = useTranslation('pmFinancials');

  const segments = CATEGORY_IDS.map((id) => {
    const category = activeFinancials.categories.find((candidate) => candidate.id === id)!;
    return {
      id,
      pct: !category.hidden && !category.billedVia && category.available ? category.sharePct : 0,
    };
  }).filter((segment) => segment.pct > 0);
  const distributedPct = segments.reduce((sum, segment) => sum + segment.pct, 0);
  const remainderPct = Math.max(0, 100 - distributedPct);
  const overflowPct = Math.max(0, distributedPct - 100);

  return (
    <Box data-automation-id="revenue-distribution">
      <Box
        sx={{
          fontSize: 11,
          letterSpacing: MICRO_LABEL_TRACKING,
          textTransform: 'uppercase',
          color: 'var(--fg-3)',
          mb: 0.5,
        }}
      >
        {t('distribution.Title')}
      </Box>
      <Box
        data-automation-id="distribution-bar"
        sx={{
          height: 6,
          borderRadius: '100px',
          overflow: 'hidden',
          display: 'flex',
          bgcolor: 'var(--bg-sunken)',
          mb: 0.5,
        }}
      >
        {segments.map((segment) => (
          <Box
            key={segment.id}
            sx={{
              height: '100%',
              width: `${(segment.pct / Math.max(100, distributedPct)) * 100}%`,
              bgcolor: CATEGORY_COLORS[segment.id],
              flex: 'none',
              transition: 'width var(--duration-slow) var(--easing-standard)',
            }}
          />
        ))}
        {remainderPct > 0 && revenue > 0 ? (
          <Box
            sx={{
              height: '100%',
              width: `${remainderPct}%`,
              bgcolor: 'var(--color-fog-20)',
              flex: 'none',
            }}
          />
        ) : null}
        {overflowPct > 0 ? (
          <Box
            data-automation-id="distribution-overflow"
            sx={{
              height: '100%',
              width: `${(overflowPct / Math.max(100, distributedPct)) * 100}%`,
              bgcolor: 'var(--color-coral-80)',
              flex: 'none',
            }}
          />
        ) : null}
      </Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {segments.map((segment) => (
          <Box
            key={segment.id}
            component="span"
            sx={{
              fontSize: 11,
              color: 'var(--fg-3)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              whiteSpace: 'nowrap',
            }}
          >
            <Box
              component="span"
              sx={{
                width: 5,
                height: 5,
                borderRadius: '50%',
                bgcolor: CATEGORY_COLORS[segment.id],
                display: 'inline-block',
                flex: 'none',
              }}
            />
            {t('distribution.LegendItem', {
              category: t(`distribution.Legend.${segment.id}`),
              pct: Math.round(segment.pct),
            })}
          </Box>
        ))}
        {revenue > 0 && remainderPct > 0 ? (
          <Box component="span" sx={{ fontSize: 11, color: 'var(--fg-3)' }}>
            {t('distribution.Remaining', {
              amount: formatAmountAbbrev(activeFinancials.notAllocated),
            })}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};
